/* Extend Minimog's collection controller without changing search or other grids. */
class TiworksCollection extends Collection {
  init = () => {
    this.domNodes = queryDomNodes(this.selectors);
    if (!this.domNodes.section) return;
    this.setData();
    this.initFilters();
    this.initProducts();
    this.fitProductImages();
    if (!this.sideEffectEventsAdded) {
      this.drawerKeydown = (event) => {
        if (!this.domNodes.section.classList.contains('sidebar-open')) return;
        if (event.key === 'Escape') this.closeSidebarFilter();
        if (event.key !== 'Tab') return;
        const focusable = [...this.domNodes.sidebarContent.querySelectorAll('a[href],button,select,input,summary')]
          .filter(node => !node.disabled && node.getClientRects().length);
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      };
      document.addEventListener('keydown', this.drawerKeydown);
      window.addEventListener('resize', () => {
        if (window.innerWidth >= 1024 && this.domNodes.section.classList.contains('sidebar-open')) this.closeSidebarFilter();
      });
      MinimogEvents.subscribe('ON_PRODUCT_LIST_UPDATED', () => refreshProductReview());
      this.sideEffectEventsAdded = true;
    }
  };

  // The current bolt images have white margins. Measure each loaded image rather
  // than applying a blind crop; unrecognized images keep the theme's contain fit.
  fitProductImages = () => {
    this.imageFitCache ||= new Map();
    const schedule = window.requestIdleCallback || (callback => setTimeout(callback, 0));
    const fit = image => schedule(() => {
      if (!image.isConnected || !image.complete || !image.naturalWidth) return;
      const source = image.currentSrc || image.src;
      image.style.removeProperty('--tw-image-scale');
      if (!source.includes('/tiworks_clean_')) return;
      if (this.imageFitCache.has(source)) {
        image.style.setProperty('--tw-image-scale', this.imageFitCache.get(source));
        return;
      }
      const width = image.naturalWidth, height = image.naturalHeight;
      if (Math.abs(width / height - 1) > .01 || width > 1024 || height > 1024) return;
      try {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d', {willReadFrequently: true});
        context.fillStyle = '#fff';
        context.fillRect(0, 0, width, height);
        context.drawImage(image, 0, 0);
        const pixels = context.getImageData(0, 0, width, height).data;
        let left = width, top = height, right = -1, bottom = -1;
        for (let y = 0; y < height; y++) {
          for (let x = 0; x < width; x++) {
            const i = (y * width + x) * 4;
            // Include even very faint shadows. Only pure white is empty space.
            if (pixels[i] === 255 && pixels[i + 1] === 255 && pixels[i + 2] === 255) continue;
            left = Math.min(left, x); right = Math.max(right, x + 1);
            top = Math.min(top, y); bottom = Math.max(bottom, y + 1);
          }
        }
        if (right < 0) return;
        const radius = Math.max(Math.abs(left / width - .5), Math.abs(right / width - .5),
          Math.abs(top / height - .5), Math.abs(bottom / height - .5));
        // At least 10% of the frame remains clear beyond every product pixel.
        const scale = Math.max(1, Math.min(1.7, .4 / radius));
        this.imageFitCache.set(source, scale);
        image.style.setProperty('--tw-image-scale', scale);
      } catch (_) {
        // Cross-origin or unreadable pixels fall back to the original safe fit.
      }
    });
    this.domNodes.section.querySelectorAll('.m-product-card__main-image img,.m-product-card__hover-image img').forEach(image => {
      if (image.dataset.twFitBound) return;
      image.dataset.twFitBound = 'true';
      image.addEventListener('load', () => fit(image));
      if (image.complete) fit(image);
    });
  };

  initFilters = () => {
    const { sidebar, openSidebar, closeSidebar, filtersWrapper } = this.domNodes;
    if (filtersWrapper) window.requestAnimationFrame(this.initAccordions);
    if (!this.enableFilters || !sidebar || !openSidebar || !closeSidebar) return;
    openSidebar.addEventListener('click', this.openSidebarFilter);
    closeSidebar.addEventListener('click', this.closeSidebarFilter);
    sidebar.addEventListener('click', event => {
      if (event.target === sidebar || event.target.closest('.tw-category-nav a')) this.closeSidebarFilter();
    });
  };

  openSidebarFilter = () => {
    const { sidebar, sidebarContent, section, openSidebar, closeSidebar } = this.domNodes;
    if (!sidebar || window.innerWidth >= 1024) return;
    clearTimeout(this.drawerTimer);
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    sidebar.style.display = 'block';
    sidebarContent.setAttribute('role', 'dialog');
    sidebarContent.setAttribute('aria-modal', 'true');
    sidebarContent.setAttribute('aria-label', 'Filter products');
    openSidebar.setAttribute('aria-expanded', 'true');
    section.classList.add('sidebar-open');
    window.requestAnimationFrame(() => {
      sidebar.style.setProperty('--m-bg-opacity', '0.5');
      sidebarContent.style.setProperty('--m-translate-x', '0');
      this.accordions?.setContentHeight();
      closeSidebar.focus();
    });
  };

  closeSidebarFilter = () => {
    const { sidebar, sidebarContent, section, openSidebar } = this.domNodes;
    if (!section.classList.contains('sidebar-open')) return;
    section.classList.remove('sidebar-open');
    document.body.style.overflow = this.previousBodyOverflow || '';
    sidebarContent.removeAttribute('role');
    sidebarContent.removeAttribute('aria-modal');
    sidebarContent.removeAttribute('aria-label');
    sidebarContent.style.setProperty('--m-translate-x', '-100%');
    sidebar.style.removeProperty('--m-bg-opacity');
    openSidebar?.setAttribute('aria-expanded', 'false');
    openSidebar?.focus();
    this.drawerTimer = setTimeout(() => sidebar.style.removeProperty('display'), 200);
  };
}
